// moment-timezone registers .tz on the shared moment instance;
// getDateRangeInTimeZone needs it, so import it here rather than relying on a
// lazy view (Frauds, ExchangeAnalytics) having loaded it first.
import moment from 'moment-timezone'

const REQUIRED_FIELD_ERROR_MESSAGE = 'Please enter a valid value'

function dateComparatorForDesc(date1, date2) {
  var date1Number = date1 && new Date(date1).getTime()
  var date2Number = date2 && new Date(date2).getTime()

  if (date1Number == null && date2Number == null) {
    return 0
  }

  if (date1Number == null) {
    return 1
  } else if (date2Number == null) {
    return -1
  }

  return date2Number - date1Number
}

function dateComparatorForAsc(date1, date2) {
  var date1Number = date1 && new Date(date1).getTime()
  var date2Number = date2 && new Date(date2).getTime()

  if (date1Number == null && date2Number == null) {
    return 0
  }

  if (date1Number == null) {
    return -1
  } else if (date2Number == null) {
    return 1
  }

  return date1Number - date2Number
}

export function replaceItemInArray(items, item, findItem) {
  const idx = items.findIndex(findItem)

  items[idx] = item
}

/**
 * Coerce an API payload that is meant to be a collection into an array.
 *
 * Store state that backs a `v-for` or a `.map()`/`.filter()` getter has to stay
 * an array. When an endpoint answers with an object (or a scalar), a raw
 * assignment survives until the first render and then takes the whole route
 * down - `state.allSellersData?.filter is not a function`, or a `v-for` walking
 * an object's values and dereferencing `null.id`. Optional chaining does not
 * help: it guards null, not "not an array". Normalise at the assignment so an
 * unexpected payload degrades to an empty list.
 *
 * @template T
 * @param {unknown} value
 * @returns {T[]}
 */
export function asArray(value) {
  return Array.isArray(value) ? value : []
}

// ---------------------------------------------------------------------------
// Canonical date/time display formats.
//
// Every user-facing date in the app must be rendered through one of the three
// `formatDate*` helpers below so the formats cannot drift apart per page
// (see finding F15 in docs/qa-staging-preview-findings.md).
//
//   formatDate            -> May 6, 2026
//   formatDateTime        -> May 6, 2026, 3:47 PM      <- default
//   formatDateTimeWithSeconds -> May 6, 2026, 3:47:52 PM  <- audit/event logs only
//
// The locale is pinned to en-US on purpose: leaving it to the browser makes the
// output depend on the viewer's OS settings (e.g. `11/8/2026, 11:30:13 am`).
// None of these set a `timeZone`, so all three render in the browser's local
// zone — that is the pre-existing behaviour and must not be changed here.
// ---------------------------------------------------------------------------

// Deliberately not exported: callers must go through the helpers below so a
// page cannot quietly introduce a fourth format.
const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short'
})

const dateFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium'
})

const dateTimeWithSecondsFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'medium'
})

function formatWith(formatter, date, fallback) {
  if (date === null || date === undefined || date === '') {
    return fallback
  }

  const parsed = date instanceof Date ? date : new Date(date)

  // `new Date('nonsense')` yields an Invalid Date, which makes Intl throw.
  if (isNaN(parsed.getTime())) {
    return fallback
  }

  return formatter.format(parsed)
}

export function getDateSortOperator(getValue = undefined, sort = 'desc') {
  const sortFunc =
    sort === 'desc' ? dateComparatorForDesc : dateComparatorForAsc

  if (getValue) {
    return function(dataA, dataB) {
      return sortFunc(getValue(dataA), getValue(dataB))
    }
  }

  return sortFunc
}

export function capitlizeFirstLetter(str) {
  return str && str.length > 0 ? `${str[0].toUpperCase()}${str.slice(1)}` : ''
}

/** Date only, e.g. `May 6, 2026`. Use when the time of day is not meaningful. */
export function formatDate(date, fallback = '') {
  return formatWith(dateFormat, date, fallback)
}

/** Date + time, e.g. `May 6, 2026, 3:47 PM`. The default for anything timestamped. */
export function formatDateTime(date, fallback = '') {
  return formatWith(dateTimeFormat, date, fallback)
}

/**
 * Date + time to the second, e.g. `May 6, 2026, 3:47:52 PM`.
 * Reserved for audit / event logs where two entries can share a minute and
 * their order matters. Do not use it for ordinary list columns.
 */
export function formatDateTimeWithSeconds(date, fallback = '') {
  return formatWith(dateTimeWithSecondsFormat, date, fallback)
}

export function timeAgo(date) {
  if (!date) {
    return ''
  }

  return moment(new Date(date)).fromNow()
}

// Queue-age emphasis for review lists: amber past warnDays, red past dangerDays.
export function queueAgeClass(date, { warnDays = 14, dangerDays = 30 } = {}) {
  if (!date) {
    return ''
  }

  const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000)

  if (days > dangerDays) {
    return 'text-danger fw-medium'
  }

  if (days > warnDays) {
    return 'text-warning fw-medium'
  }

  return ''
}

// Missing date is not "old": items with bad data must stay visible.
export function isOlderThanDays(date, days) {
  if (!date) {
    return false
  }

  return (Date.now() - new Date(date).getTime()) / 86400000 > days
}

export function convertDurationInSecsToTimeFormat(durationInSecs) {
  return moment.utc(durationInSecs * 1000).format('HH:mm:ss')
}

export function formatDateInclusive(date) {
  if (!date || date === 'N/A') {
    return 'N/A'
  }

  const result = moment.parseZone(date)
  if (!result.isValid()) {
    return 'N/A'
  }

  result.subtract(1, 's')
  // `MMM D, YYYY` is moment's spelling of the canonical `formatDate` output.
  // moment is kept here (rather than reusing formatDate) because parseZone
  // deliberately renders in the offset embedded in the string, not local time.
  return result.format('MMM D, YYYY')
}

export function formatAmount(amount) {
  if (amount === undefined) { 
    return "N/A"
  }
  
  var formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })
  return formatter.format(amount)
}

// Throws unless the amount about to be sent equals the amount on screen.
// The currency mask rewrites the input outside Vue, so if it ever stops syncing
// the model the UI still looks right while the wrong number gets POSTed.
export function assertAmountMatchesDisplay(inputEl, amount) {
  const displayed = Number(String(inputEl?.value ?? '').replace(/[^0-9.-]/g, ''))

  if (!Number.isFinite(displayed) || !Number.isFinite(amount) || Math.round(displayed * 100) !== Math.round(amount * 100)) {
    throw new Error(`Amount mismatch: the field shows ${formatAmount(displayed)} but ${formatAmount(amount)} would be sent. Please re-enter the amount.`)
  }
}

export function formatPercentage(amount) {
  if (amount === undefined) { 
    return "N/A"
  }

  var formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 2
  })
  return formatter.format(amount)
}

export function sortAlphabeticallyComparison(string1, string2) {
  if (string1 < string2) {
    return -1
  }

  if (string1 > string2) {
    return 1
  }

  // strings must be equal
  return 0
}

export function sortByKey(key) {
  return (a, b) => sortAlphabeticallyComparison((a[key] || '').toUpperCase(), (b[key] || '').toUpperCase())
}

// Unordered array equality.
function sameMembers(a, b) {
  return a.length === b.length && a.every(value => b.includes(value))
}

// CMultiSelect echoes a change event when options and values sync up on load.
// Returns the selected values, or null when they match the current model.
export function changedSelection(selected, current) {
  const values = selected.map(option => option.value)
  return sameMembers(values, current || []) ? null : values
}

// CMultiSelect emits an array of option objects even in single-select mode.
export function firstSelectedValue(selected, fallback = null) {
  return selected.length ? selected[0].value : fallback
}

// First selected option as { id, label }, or null when the selection is cleared.
export function firstSelectedOption(selected) {
  return selected.length ? { id: selected[0].value, label: selected[0].label } : null
}

// CoreUI 5 CFormSelect dropped the v3 placeholder prop and skips the value
// binding for falsy models, so the browser would show the first real option.
// Prepend an empty-value option so the display matches an unset model.
export function withPlaceholderOption(options, label = 'Please select') {
  return [{ value: '', label }, ...(options ?? [])]
}

// window.open() keeps window.opener set (anchors get implicit noopener,
// window.open does not). Browsers special-case the noopener token, so this
// still opens a tab, not a popup.
export function openInNewTab(url) {
  window.open(url, '_blank', 'noopener')
}

// InvoiceStatus values (RingbaX.Services.Billing.Models.InvoiceStatus) reached
// BEFORE the invoice PDF is rendered to storage. The admin invoice list stopped
// filtering these out (sp_getBuyerPayments/sp_getSellerPayments dropped their
// `invoiceStatusId >= Issued` clause so failures stay visible), but
// GET admin/payments/invoice/{accountId}/{invoiceId} only presigns an existing
// {accountId}/{invoiceId}.pdf object - for these it answers a bare 404.
const INVOICE_STATUSES_WITHOUT_PDF = ['GeneratePdfPending', 'GeneratePdfFailed']

export const NO_INVOICE_PDF_MESSAGE = 'No PDF is available for this invoice yet.'

// True when the invoice is far enough along that a PDF exists to download.
// Anything past PDF generation (Issued, and every Verification* state) has one,
// so this stays a blocklist - an allow-list would wrongly drop the
// VerificationPending/Succeeded/Failed states, which all do have a PDF.
//
// A missing status is treated as "no PDF" rather than "probably fine": every
// real caller reads a row off the admin invoice list, where InvoiceModel's
// non-nullable InvoiceStatus enum guarantees the field, so an absent status
// means a partially-populated object and offering a download for it could only
// ever 404.
export function hasInvoicePdf(invoice) {
  const status = invoice?.invoiceStatusId

  if (!status) {
    return false
  }

  return !INVOICE_STATUSES_WITHOUT_PDF.includes(status)
}

// The download endpoint's 404 carries no body, so httpClient's interceptor
// stays silent and the raw AxiosError reaches the caller - turn it into
// something a human can act on instead of an unhandled rejection.
export function invoicePdfErrorMessage(error, notFoundMessage = NO_INVOICE_PDF_MESSAGE) {
  if (error?.response?.status === 404) {
    return notFoundMessage
  }

  return error?.response?.data?.title || error?.message || notFoundMessage
}

export const sortByLabel = sortByKey('label')

export const sortByName = sortByKey('name')

// CoreUI 5 form controls ignore v-model modifiers (.trim/.number leak into the
// DOM as a modelmodifiers attribute), so normalize values at submit time instead.
export function trimmed(value) {
  return typeof value === 'string' ? value.trim() : value
}

export function toNumberOrNull(value) {
  if (value == null || value === '') {
    return null
  }

  const number = Number(value)

  return Number.isNaN(number) ? value : number
}

// Group items into { label, options } groups keyed by getGroupName,
// sorted alphabetically by group label.
function groupIntoLabeledOptions(items, getGroupName) {
  const groups = []
  const byName = {}

  items.forEach(item => {
    const name = getGroupName(item) || ''

    if (!byName[name]) {
      byName[name] = { label: name, options: [] }
      groups.push(byName[name])
    }

    byName[name].options.push({ value: item.id, label: item.name })
  })

  return groups.sort((a, b) => a.label.localeCompare(b.label))
}

export function groupTrafficSourcesByType(sources) {
  return groupIntoLabeledOptions(sources, source => source?.trafficSourceType)
}

export function groupCategoriesByParent(categories) {
  return groupIntoLabeledOptions(categories, category => category?.parentCategory?.name)
}

export function getDateRangeInTimeZone(timeRange) {
  const standardTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
  // Time is sent in UTC
  // If user selects 3PM in PST(UTC-08:00): the time which would be sent is 15:00 - 08:00 = 07:00 
  const startDate = moment.tz(moment(timeRange.startDate).format('YYYY-MM-DD HH:mm:ss'), standardTimeZoneId).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
  const endDate = moment.tz(moment(timeRange.endDate).format('YYYY-MM-DD HH:mm:ss'), standardTimeZoneId).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')

  return { startDate, endDate }
}

export function splitLines(t) {
  return t.split(/\r\n|\r|\n/)
}

// Escape &, <, >, ", ' for safe interpolation into HTML strings.
export function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Shared tail of the CSV exporters: one text/csv Blob, one synthetic anchor
// click, object URL released straight after. Kept private so each exporter
// still owns its own headers, cell shaping and file naming.
function downloadCsvRows(csvRows, filename) {
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

// Every cell is quoted (dates render as "May 6, 2026, 3:47 PM", so commas are
// the norm here, not the exception) and embedded quotes are doubled per RFC 4180.
function csvCell(value) {
  return `"${String(value != null ? value : '').replace(/"/g, '""')}"`
}

// This is the exact column set the account manager asked for - nothing from
// the accounts list beyond it. Do not grow this back into a general accounts
// export: Company, Contact, Email, Phone, Messenger and Categories are it.
// The backend's account-manager/my-contacts endpoint already resolves every
// one of these (joined representative name, category ids turned into names),
// so this just writes the row it was given - no lookups, no reshaping.
const ACCOUNT_CONTACT_COLUMNS = [
  // One file covers both sides of the exchange, so the type has to travel with
  // the row - an account manager can hold buyers and sellers at once.
  { label: 'Account Type', value: row => row.accountType },
  { label: 'Company', value: row => row.company },
  { label: 'Contact', value: row => row.contact },
  { label: 'Email', value: row => row.email },
  { label: 'Phone', value: row => row.phone },
  { label: 'Messenger', value: row => row.messenger },
  // Commas already separate the columns, so the names inside this one cell
  // are separated by semicolons.
  { label: 'Categories', value: row => (row.categories || []).join('; ') }
]

/**
 * Contact sheet for a set of account contacts, for upload into a CRM.
 *
 * @param {Object[]} rows Contacts as returned by
 *   user-accounts/account-manager/my-contacts, already carrying accountType,
 *   company, contact, email, phone, messenger and resolved category names
 * @param {string} filePrefix Leads the file name, e.g. 'my'
 */
export function exportAccountContactsCsv({ rows, filePrefix }) {
  if (!rows.length) {
    return
  }

  const csvRows = [ACCOUNT_CONTACT_COLUMNS.map(column => column.label).join(',')]

  for (const row of rows) {
    csvRows.push(ACCOUNT_CONTACT_COLUMNS.map(column => csvCell(column.value(row))).join(','))
  }

  downloadCsvRows(csvRows, `${filePrefix}-account-contacts-${new Date().toISOString().slice(0, 10)}.csv`)
}

export function exportPaymentHistoryCsv({ rows, fields, filePrefix }) {
  if (!rows.length) {
    return
  }

  const headers = fields.map(f => f.label || f.key)
  const keys = fields.map(f => f.key)

  const csvRows = [headers.join(',')]
  for (const row of rows) {
    const values = keys.map(key => {
      let val = ''
      if (key === 'paymentDate') {
        val = row.formattedPaymentDate || ''
      } else {
        val = row[key] != null ? row[key] : ''
      }

      const escaped = String(val).replace(/"/g, '""')
      return `"${escaped}"`
    })
    csvRows.push(values.join(','))
  }

  downloadCsvRows(csvRows, `${filePrefix}-payment-history-${new Date().toISOString().slice(0, 10)}.csv`)
}

export function exportInvoiceCallsCsv({ billableCalls, disputedCalls, accountId, invoiceId }) {
  if (!billableCalls.length && !disputedCalls.length) {
    return
  }

  const headers = ['Type', 'Call ID', 'Caller Phone', 'Call Date', 'Disputed On', 'Category', 'Amount', 'Adjustment Amount', 'Dispute Fee']
  const escape = val => `"${String(val != null ? val : '').replace(/"/g, '""')}"`

  const csvRows = [headers.join(',')]

  for (const call of billableCalls) {
    csvRows.push([
      'Billable', call.callId, call.callerId, call.callDate, '', call.category, call.amount, '', ''
    ].map(escape).join(','))
  }

  for (const call of disputedCalls) {
    csvRows.push([
      'Disputed', call.callId, call.callerId, call.callDate, call.disputedOn, call.category, '', call.adjustmentAmount, call.disputeFee
    ].map(escape).join(','))
  }

  downloadCsvRows(csvRows, `invoice-${accountId}-${invoiceId}-calls-${new Date().toISOString().slice(0, 10)}.csv`)
}

export { REQUIRED_FIELD_ERROR_MESSAGE }
